/**
 * Environments where the design-system Playground page may appear.
 * Allowlist only — missing, empty, unknown, or 'production' are all hidden.
 */
export const PLAYGROUND_ENVIRONMENTS = ['development', 'uat'] as const;

/**
 * Hosts the Playground may be served from. UAT and prod live in separate GCP
 * projects on fixed domains, and there are no preview or ephemeral deploys, so
 * this set is closed rather than a pattern.
 */
export const PLAYGROUND_HOSTS = ['localhost', '127.0.0.1', 'uat.theteoai.com'] as const;

/** True when `APP_CONFIG.ENVIRONMENT` names a non-production environment. */
export function isPlaygroundEnvironmentValue(): boolean {
  const env = globalThis.window?.APP_CONFIG?.ENVIRONMENT;
  return env !== undefined && (PLAYGROUND_ENVIRONMENTS as readonly string[]).includes(env);
}

/**
 * True when the page is being served from a non-production host. Off the
 * browser there is no host to vouch for, so the answer is no rather than a
 * thrown reference — the gate has to stay closed, not take the caller down.
 */
export function isPlaygroundHost(): boolean {
  const hostname = globalThis.window?.location?.hostname;
  return hostname !== undefined && (PLAYGROUND_HOSTS as readonly string[]).includes(hostname);
}

/**
 * Fail-closed gate for the admin Playground page.
 *
 * Two independent signals must agree, because prod runs the *same image* UAT
 * built — `promote-frontend` redeploys the UAT artifact into the prod project,
 * so the deploy-time env var is the only thing distinguishing them. If that var
 * were ever wrong on the prod service, the environment check alone would expose
 * the page; the host check is the signal that cannot be misconfigured by a bad
 * promote, and vice versa for a domain served with the wrong config.
 *
 * Both checks are allowlists, so an unrecognised environment or an unexpected
 * host keeps the page hidden rather than revealing it.
 */
export function isPlaygroundEnvironment(): boolean {
  return isPlaygroundEnvironmentValue() && isPlaygroundHost();
}
